import React, { createContext, useContext, useEffect, useState } from "react";
import Echo from "laravel-echo";
import Pusher from "pusher-js";
import { Constants } from "./constants";
import { useAuthService } from "./authservice";

// Pusher must be globally available for laravel-echo's Reverb broadcaster
(window as any).Pusher = Pusher;

const EchoContext = createContext<Echo<any> | null>(null);

export function EchoProvider({ children }: { children: React.ReactNode }) {
    const authService = useAuthService();
    const [echoInstance, setEchoInstance] = useState<Echo<any> | null>(null);

    useEffect(() => {
        const authEndpoint = Constants.API_URL + "/broadcasting/auth";
        const token = authService.getAuthToken();

        const instance = new Echo({
            broadcaster: "reverb",
            key: Constants.REVERB_APP_KEY,
            wsHost: Constants.REVERB_HOST,
            wsPort: Constants.REVERB_PORT,
            wssPort: Constants.REVERB_PORT,
            forceTLS: Constants.REVERB_PORT === 443,
            enabledTransports: ["ws", "wss"],
            authorizer: (channel: any) => ({
                authorize: (socketId: string, callback: Function) => {
                    fetch(authEndpoint, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Accept: "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({
                            socket_id: socketId,
                            channel_name: channel.name,
                        }),
                    })
                        .then((response) => {
                            if (!response.ok) {
                                throw new Error(`Auth failed: ${response.status}`);
                            }
                            return response.json();
                        })
                        .then((data) => callback(null, data))
                        .catch((error) => {
                            console.error("[Echo] Channel auth error:", error);
                            callback(error, null);
                        });
                },
            }),
        });

        setEchoInstance(instance);

        const pusher = (instance.connector as any).pusher;

        if (pusher?.connection) {
            pusher.connection.bind("connected", () => {
                console.log("[Echo] Connected to Reverb");
            });

            pusher.connection.bind("disconnected", () => {
                console.log("[Echo] Disconnected from Reverb");
            });

            pusher.connection.bind("error", (err: any) => {
                console.error("[Echo] Connection error:", err);
            });

            pusher.connection.bind("state_change", (states: any) => {
                console.log(
                    `[Echo] State changed: ${states.previous} -> ${states.current}`
                );
            });
        }

        console.log("[Echo] Initialised — connecting to", Constants.REVERB_HOST);

        const channel = instance.private("warehouse");

        channel.listenToAll((event: string, data: any) => {
            console.log(`[Echo] Event on private-warehouse:`, event, data);
        });

        console.log("[Echo] Subscribed to private-warehouse channel");

        return () => {
            instance.leave("warehouse");
            instance.disconnect();
            setEchoInstance(null);
            console.log("[Echo] Disconnected (cleanup)");
        };
    }, []);

    return (
        <EchoContext.Provider value={echoInstance}>
            {children}
        </EchoContext.Provider>
    );
}

export function useEcho(): Echo<any> | null {
    return useContext(EchoContext);
}
